import { BACKEND_URL } from '../config/backendUrl';
/**
 * EmailDeliverabilitySection — Settings card that exposes the
 * deliverability diagnostic tools.
 *
 * Two cards stacked:
 *  1. "Send a test email" — pastes a mail-tester.com address (or any
 *     real mailbox) and fires one of the three template variants.
 *  2. "DNS checklist" — server-returned checklist with copyable `dig`
 *     commands and direct mxtoolbox links so the admin can verify
 *     SPF / DKIM / DMARC themselves.
 */
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Mail,
  CheckCircle2,
  ExternalLink,
  Send,
  RefreshCw,
  Copy as CopyIcon,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${BACKEND_URL}/api`;

export default function EmailDeliverabilitySection() {
  const [toEmail, setToEmail] = useState('');
  const [template, setTemplate] = useState('document');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(true);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [chk, dns] = await Promise.all([
          axios.get(`${API_URL}/admin/email-diagnostics/header-checklist`),
          axios.get(`${API_URL}/admin/email-diagnostics/dns-audit`),
        ]);
        if (!alive) return;
        setChecklist(chk.data);
        setAudit(dns.data);
      } catch (e) {
        // 403 just means non-admin — silently hide
      } finally {
        if (alive) setLoadingChecklist(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const refreshAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/email-diagnostics/dns-audit`);
      setAudit(data);
      toast.success('DNS re-checked');
    } catch (e) {
      toast.error('Could not refresh DNS audit');
    } finally {
      setAuditing(false);
    }
  };

  const handleSendTest = async () => {
    if (!toEmail || !toEmail.includes('@')) {
      toast.error('Enter a valid email address first');
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/admin/email-diagnostics/send-test`, {
        to_email: toEmail,
        template,
      });
      setLastResult(data);
      if (data.status === 'sent') {
        toast.success(`Test email sent — check ${toEmail} in ~30 seconds.`);
      } else if (data.status === 'mocked') {
        toast.warning('Email service not configured (key missing) — send was mocked.');
      } else if (data.status === 'suppressed') {
        toast.warning('That address previously unsubscribed — try another.');
      } else {
        toast.error(`Send failed: ${data.message || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send test email');
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loadingChecklist) return null;
  if (!checklist) return null; // non-admin

  return (
    <div className="neumorphic rounded-xl p-6 space-y-6" data-testid="email-deliverability-card">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-600 to-cyan-700 flex items-center justify-center">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg">Email Deliverability</h2>
          <p className="text-sm text-muted-foreground">
            Fire a test email through the real send pipeline and verify your SPF / DKIM / DMARC
            DNS records. The #1 cause of Hotmail / Outlook junk placement is authentication
            mis-alignment, not content.
          </p>
        </div>
      </div>

      {/* Send a test */}
      <div className="border-t border-slate-200 pt-5 space-y-3">
        <div>
          <Label className="text-sm font-medium">Send a test email</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Paste a <a href="https://www.mail-tester.com" target="_blank" rel="noreferrer" className="text-sky-600 underline">mail-tester.com</a> throwaway address
            (or any real mailbox) and pick a template. After ~30 seconds, refresh the mail-tester results page to see SPF/DKIM/DMARC pass-fail and the full junk-filter score.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="test-abcd1234@srv1.mail-tester.com"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            className="flex-1 font-mono text-sm"
            data-testid="deliverability-test-email-input"
          />
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            data-testid="deliverability-template-select"
          >
            <option value="document">Send packet (primary)</option>
            <option value="reminder">Reminder</option>
            <option value="completion">Completion confirmation</option>
          </select>
          <Button
            onClick={handleSendTest}
            disabled={sending}
            data-testid="deliverability-send-test-btn"
          >
            {sending ? (
              <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-4 h-4 mr-1.5" /> Send test</>
            )}
          </Button>
        </div>

        {lastResult && (
          <div className={`mt-2 p-3 rounded-lg text-xs ${
            lastResult.status === 'sent'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border border-amber-200 text-amber-800'
          }`}>
            <div className="font-medium">
              Status: {lastResult.status} · Template: {lastResult.template}
              {lastResult.email_id && (
                <span className="font-mono ml-2">id={lastResult.email_id}</span>
              )}
            </div>
            <div className="mt-1 opacity-80">{lastResult.message}</div>
          </div>
        )}
      </div>

      {/* What we already do */}
      <div className="border-t border-slate-200 pt-5">
        <Label className="text-sm font-medium">Sender-side hardening (already on)</Label>
        <ul className="mt-2 space-y-1.5 text-xs">
          {Object.entries(checklist.headers_emitted_by_hyredocs || {}).map(([k, v]) => (
            <li key={k} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-mono text-slate-700">{k}</span>
                <span className="text-slate-500 ml-1">— {String(v).replace(/^✓\s*/, '')}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Live DNS audit */}
      <div className="border-t border-slate-200 pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-sm font-medium">Live DNS audit</Label>
            {audit?.sender?.from_address && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Resolving SPF / DKIM / DMARC / MX for the actual sending address: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{audit.sender.from_address}</code>
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAudit}
            disabled={auditing}
            data-testid="dns-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${auditing ? 'animate-spin' : ''}`} />
            Re-check
          </Button>
        </div>

        {audit?.summary && (
          <div className={`mb-3 p-2.5 rounded-lg text-xs font-medium ${
            audit.summary.overall === 'pass'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : audit.summary.overall === 'warn'
              ? 'bg-amber-50 text-amber-800 border border-amber-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`} data-testid="dns-audit-summary">
            Overall: <strong className="uppercase">{audit.summary.overall}</strong>
            {' · '}
            <span className="text-emerald-700">{audit.summary.passes} passing</span>
            {audit.summary.warns > 0 && <span className="text-amber-700">{' · '}{audit.summary.warns} warning</span>}
            {audit.summary.fails > 0 && <span className="text-red-700">{' · '}{audit.summary.fails} failing</span>}
          </div>
        )}

        <div className="space-y-2">
          {(audit?.checks || []).map((check, idx) => {
            const cfg = {
              pass: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CheckCircle2, iconClass: 'text-emerald-600' },
              warn: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconClass: 'text-amber-600' },
              fail: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: AlertTriangle, iconClass: 'text-red-600' },
              info: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: ExternalLink, iconClass: 'text-slate-500' },
            }[check.status] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: ExternalLink, iconClass: 'text-slate-500' };
            const StatusIcon = cfg.icon;
            return (
              <div
                key={idx}
                className={`rounded-lg border ${cfg.border} ${cfg.bg} p-3`}
                data-testid={`dns-audit-check-${idx}`}
              >
                <div className="flex items-start gap-2">
                  <StatusIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.iconClass}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${cfg.text}`}>
                      {check.name}
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-white/60 rounded">
                        {check.status}
                      </span>
                    </div>
                    {check.value && (
                      <div className="mt-1 flex items-start gap-1.5">
                        <code className="flex-1 text-[11px] bg-white/70 px-2 py-1 rounded font-mono text-slate-700 break-all">
                          {Array.isArray(check.value) ? check.value.join(', ') : String(check.value)}
                        </code>
                        <button
                          onClick={() => copyToClipboard(Array.isArray(check.value) ? check.value.join(', ') : String(check.value))}
                          className="p-1 text-slate-400 hover:text-slate-600 flex-shrink-0"
                        >
                          <CopyIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {check.note && (
                      <p className={`text-[11px] mt-1.5 ${cfg.text} opacity-90`}>{check.note}</p>
                    )}
                    {check.fix && (
                      <p className="text-[11px] mt-1.5 text-slate-600">
                        <span className="font-semibold">Fix:</span> {check.fix}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* If still junked */}
      <div className="border-t border-slate-200 pt-5">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <Label className="text-sm font-medium">If Outlook still junks after fixing DNS</Label>
            <ul className="mt-2 space-y-1.5 text-xs text-slate-600 list-disc list-inside">
              {(checklist.next_steps_if_outlook_still_junks || []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
